import React from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { Draggable } from 'react-beautiful-dnd';
import classnames from 'classnames';
import ContentActionsRowMenu from '../../common/components/ContentActions/ContentActionsRowMenu/ContentActionsRowMenu';
import {
  contentShape,
  contentTypeShape,
} from '../../common/components/proptypes/commonProptypes';
import DisplayContentStatus from './DisplayContentStatus';
import DisplayDate from './DisplayDate';
import TagList from '../analyses/common/TagList/TagList';
import ContentLink from './ContentLink';
import SortOrderCell from './SortOrderCell';
import './ContentListCard.scss';
import AvatarWithMenu from '../../common/components/AvatarWithMenu/index.tsx';

const ContentListCard = ({
  contentItem,
  index,
  projectId,
  onDeleteMenuClick,
  onSelect,
  selectedCount,
  areAllSelected,
  isSelected,
  isDragTarget,
  isDragAllowed,
  isDragEnabled,
  isInternalUser,
  viewPathCreator,
  editPathCreator,
  linkAriaLabel,
}) => {
  const history = useHistory();
  const frontendIdentifier =
    contentItem.frontendIdentifier || contentItem.contentThreadId;

  const handleEditContent = () => {
    history.push(editPathCreator(projectId, frontendIdentifier));
  };

  const isDraggingMultiple = isDragTarget && selectedCount > 0;

  return (
    <Draggable
      draggableId={contentItem.contentThreadId.toString()}
      index={index}
      isDragDisabled={!isDragAllowed || areAllSelected}
    >
      {(provided) => {
        const cardClassnames = classnames('content-list-card', {
          draggable: isDragAllowed && !areAllSelected,
          'is-selected': isSelected,
          'is-dragging': isDragTarget,
          'is-dragging-multiple': isDraggingMultiple,
        });

        const sortOrderDisplay = isDraggingMultiple
          ? selectedCount
          : contentItem.sortOrder + 1;

        return (
          <div
            className={cardClassnames}
            data-testid="content-list-card"
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
          >
            {isInternalUser && (
              <SortOrderCell
                sortOrder={sortOrderDisplay}
                isSelected={isSelected}
                isDragAllowed={isDragAllowed}
                isDragEnabled={isDragEnabled}
                contentThreadId={contentItem.contentThreadId}
                onSelect={onSelect}
                index={index}
              />
            )}
            <ContentLink
              projectId={projectId}
              contentItem={contentItem}
              viewPathCreator={viewPathCreator}
              ariaLabel={linkAriaLabel}
            />
            {isInternalUser && (
              <>
                <DisplayDate dateText={contentItem.dateModified} />
                <DisplayContentStatus shareWithAll={contentItem.shareWithAll} />
              </>
            )}
            <div className="menu-list-wrapper">
              <AvatarWithMenu
                showInfoLockingUser
                contentThreadId={contentItem.contentThreadId}
              >
                <ContentActionsRowMenu
                  projectId={projectId}
                  itemId={frontendIdentifier}
                  onEditMenuClick={handleEditContent}
                  onDeleteMenuClick={() => onDeleteMenuClick(contentItem)}
                  direction="left"
                />
              </AvatarWithMenu>
            </div>
            {isInternalUser && (
              <TagList tags={contentItem.tags} testId="content-tag-list" />
            )}
          </div>
        );
      }}
    </Draggable>
  );
};

ContentListCard.propTypes = {
  contentType: contentTypeShape.isRequired,
  contentItem: contentShape.isRequired,
  index: PropTypes.number.isRequired,
  projectId: PropTypes.string.isRequired,
  onDeleteMenuClick: PropTypes.func,
  onSelect: PropTypes.func.isRequired,
  selectedCount: PropTypes.number,
  areAllSelected: PropTypes.bool,
  isSelected: PropTypes.bool,
  isDragTarget: PropTypes.bool,
  isDragAllowed: PropTypes.bool,
  isDragEnabled: PropTypes.bool,
  isInternalUser: PropTypes.bool,
  viewPathCreator: PropTypes.func.isRequired,
  editPathCreator: PropTypes.func.isRequired,
  linkAriaLabel: PropTypes.string.isRequired,
};

ContentListCard.defaultProps = {
  onDeleteMenuClick: () => {},
  selectedCount: 0,
  areAllSelected: false,
  isSelected: false,
  isDragTarget: false,
  isDragAllowed: false,
  isDragEnabled: false,
  isInternalUser: true,
};

export default ContentListCard;
